import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import ChatHeader from './ChatHeader';
import ChatSearchInput from './ChatSearchInput';
import ContactsList from './ContactsList';
import GroupsList from './GroupsList';
import CreateGroupModal from './CreateGroupModal';
import { useChat } from '../../contexts/ChatContext';

const STATUS_LABELS = {
  available: 'Disponível',
  busy: 'Ocupado',
  away: 'Ausente',
  offline: 'Offline'
};

/**
 * Painel esquerdo do Centro de Comunicações (FASE 1).
 * Preenche 100% do container pai — Dual-Pane Wrapper vem na FASE 2.
 * No modo flutuante atual, o ChatWidget ainda define o tamanho do wrapper.
 */
export default function ChatPanel({
  isOpen,
  onClose,
  status,
  setStatus,
  connected,
  contacts,
  presenceMap,
  loadingContacts,
  onSelectContact,
  groups,
  loadingGroups,
  onSelectGroup,
  createGroupConversation,
  openGroupConversation,
  fetchGroups,
  user,
  fotoPerfil,
  onExpand,
  hideClose = false
}) {
  const { activeChatId } = useChat();
  const [searchTerm, setSearchTerm] = useState('');
  const [createGroupOpen, setCreateGroupOpen] = useState(false);

  const handleCreateGroup = useCallback(
    async (name, participantIds) => {
      if (!createGroupConversation) return null;
      const conv = await createGroupConversation(name, participantIds);
      if (conv && openGroupConversation) {
        await openGroupConversation(conv);
        if (typeof fetchGroups === 'function') fetchGroups();
        setCreateGroupOpen(false);
        return conv;
      }
      return conv;
    },
    [createGroupConversation, openGroupConversation, fetchGroups]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  // FASE 2: quando isExpanded, este painel será a coluna esquerda (~360px) do Dual-Pane Wrapper
  return (
    <Box
      role="dialog"
      aria-label="Centro de comunicações"
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        boxShadow: { xs: 'none', sm: '0 8px 32px rgba(15,23,42,0.12)' },
        borderRadius: { xs: 0, sm: 2 }
      }}
    >
      <ChatHeader
        status={status}
        setStatus={setStatus}
        connected={connected}
        onClose={onClose}
        onExpand={onExpand}
        hideClose={hideClose}
        onNewGroup={createGroupConversation ? () => setCreateGroupOpen(true) : undefined}
        user={user}
        fotoPerfil={fotoPerfil}
      />
      <CreateGroupModal
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        contacts={contacts}
        onCreateGroup={handleCreateGroup}
        createGroupConversation={createGroupConversation}
      />

      <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          Contatos e grupos da sua unidade
        </Typography>
      </Box>

      <Box sx={{ px: 0.5, pt: 0.5, pb: 0.5, flexShrink: 0 }}>
        <ChatSearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Buscar contato..." />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {groups?.length > 0 && (
          <>
            <GroupsList
              groups={groups}
              loadingGroups={loadingGroups}
              onSelectGroup={onSelectGroup}
              activeChatId={activeChatId}
            />
            <Divider />
          </>
        )}
        {/* Clique no contato → onSelectContact abre conversa + seta activeChatId (Context).
            FASE 2: painel direito renderiza ChatWindow embutido via activeChatId */}
        <ContactsList
          contacts={contacts}
          presenceMap={presenceMap}
          loadingContacts={loadingContacts}
          onSelectContact={onSelectContact}
          statusLabels={STATUS_LABELS}
          searchTerm={searchTerm}
          activeChatId={activeChatId}
        />
      </Box>
    </Box>
  );
}
